import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormArray,
  UntypedFormControl,
  Validators,
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from "@angular/forms";
import { Observable, of } from "rxjs";
import { map, catchError, tap } from "rxjs/operators";

import { ContractService } from "../services/contract.service";
import { BankAccountsService } from "../services/bank_accounts.service";
import { VALID_EMAIL_REGEX } from "./email-validator";

export class FormsUtilities {
  public static readonly nameRegEx = /^[\p{L}\p{M}.]+[\p{Zs}\p{L}\p{M}.-]*$/u;
  public static readonly phoneRegEx = /^\d{2}[\p{Zs}]?\d{4}[\p{Zs}]?\d{4}$/u;
  public static readonly zipcodeRegEx = /^\d{5}$/u;
  public static readonly addressRegEx = /[^]*[0-9]{5}[^]*/u;
  public static readonly clabeRegEx = /^(\d{18})?$/u;
  public static readonly urlRegEx = /(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})[/\w .-]*?/u;
  public static readonly curpRegEx =
    /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/;
  // Kept as an alias of the backend-aligned regex; see email-validator.ts.
  public static readonly mailRegEx = VALID_EMAIL_REGEX;
  public static readonly ringoverIdRegEx = /^[A-Za-z0-9\s]+$/u;

  public static mark_controls_as_touched(group: UntypedFormGroup) {
    Object.values(group.controls).forEach((c: UntypedFormControl) => {
      c.markAsTouched();
    });
  }

  public static disable_all_controls(group: UntypedFormGroup) {
    Object.values(group.controls).forEach((c: UntypedFormControl) => {
      c.disable();
    });
  }

  static clabeValidator(_contractService: ContractService, callback: (clabe) => void): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      let res = of(null);
      if (control.value != "") {
        res = _contractService.clabeInfo(control.value).pipe(
          tap((result) => {
            callback(result);
          }),
          map((result: boolean) => (result ? null : { invalidClabe: true })),
          catchError(() => of({ invalidClabe: control.value }))
        );
      }
      return res;
    };
  }

  static repeatedClabeValidator(
    _bankAccountService: BankAccountsService,
    holder_id: number,
    holder_type: string,
    callback: (clabe) => void
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      let res = of(null);
      if (control.value != "") {
        res = _bankAccountService.bank_account_exists(control.value, holder_id, holder_type).pipe(
          tap((result) => {
            callback(result);
          }),
          map((result: boolean) => (result ? null : { repeatedClabe: true })),
          catchError(() => of({ repeatedClabe: control.value }))
        );
      }
      return res;
    };
  }

  public static requiredIfValidator(predicate) {
    return (formControl) => {
      if (!formControl.parent) {
        return null;
      }
      if (predicate()) {
        return Validators.required(formControl);
      }
      return null;
    };
  }
}

export class FormErrorMessages {
  public static readonly errors_in_form = "Hay errores en el formulario";
  public static readonly required = "Campo requerido";
  public static readonly pattern = "Patrón incorrecto";
  public static readonly generic = "El valor ingresado es inválido";
}
